import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import BackButton from '../../../components/common/BackButton';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { openProtectedFile } from '../../../utils/openProtectedFile';
import { apiClient, getApiErrorMessage } from '../../../services/http';
import { getJob, updateJobStatus, deleteJob as deleteJobApi } from '../../../services/jobsService';

const statusOptions = [
  { value: 'design', label: 'Design' },
  { value: 'approved', label: 'Approved' },
  { value: 'printed', label: 'Printed' },
  { value: 'ready_for_collection', label: 'Ready for Collection' },
  { value: 'collected', label: 'Collected' },
];

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountCollected, setAmountCollected] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchJob = async () => {
    try {
      const data = await getJob(id);
      setJob(data);
      setNewStatus(data.status);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load job'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateStatus = async () => {
    try {
      setUpdating(true);
      await updateJobStatus(id, { status: newStatus, notes: statusNotes, paymentMethod, amountCollected });
      toast.success('Status updated');
      await fetchJob();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to update status'));
    } finally {
      setUpdating(false);
    }
  };

  const askDelete = () => {
    setConfirmOpen(true);
  };

  const deleteJob = async () => {
    try {
      await deleteJobApi(id);
      toast.success('Job deleted successfully');
      navigate('/jobs');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to delete job'));
    } finally {
      setConfirmOpen(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        await deleteJobApi(job._id);
        navigate('/jobs');
      } catch (error) {
        console.error('Delete failed:', error);
        alert('Failed to delete job');
      }
    }
  };

  const generateDocument = async (type, jobId) => {
    try {
      const response = await apiClient.get(`/documents/${type}/${jobId}`, {
        responseType: 'blob'
      });

      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${type}-${jobId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Failed to generate ${type}:`, error);
      alert(`Failed to generate ${type}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!job) {
    return <div className="text-center text-gray-500">Job not found</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BackButton to="/jobs" label="Back" />
          <h2 className="text-2xl font-bold text-gray-900">Job {job.jobId}</h2>
        </div>
        {user?.role === 'admin' && (
          <button
            className="text-red-600 hover:text-red-800"
            onClick={askDelete}
          >
            Delete Job
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Details */}
        <div className="lg:col-span-2 space-y-6">
          <div className="relative overflow-hidden rounded-2xl shadow-lg border border-blue-200 bg-gradient-to-br from-blue-50 via-indigo-50 to-cyan-50">
            <div className="p-6">
              <h3 className="text-lg font-bold text-blue-900 mb-4">Customer Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-gray-500">Name</div>
                <div className="text-gray-900 font-medium">{job.customer?.name}</div>
              </div>
              <div>
                <div className="text-gray-500">Phone</div>
                <div className="text-gray-900 font-medium">{job.customer?.phone}</div>
              </div>
              <div>
                <div className="text-gray-500">Email</div>
                <div className="text-gray-900 font-medium">{job.customer?.email || '-'}</div>
              </div>
            </div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-2xl shadow-lg border border-purple-200 bg-gradient-to-br from-purple-50 via-fuchsia-50 to-pink-50">
            <div className="p-6">
              <h3 className="text-lg font-bold text-purple-900 mb-4">Line Items</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {job.lineItems?.map((li, idx) => (
                    <tr key={idx}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{li.serviceType}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{li.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{li.quantity}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">KSh{li.unitPrice?.toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">KSh{li.total?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-2xl shadow-lg border border-amber-200 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50">
            <div className="p-6">
              <h3 className="text-lg font-bold text-amber-900 mb-4">Attachments</h3>
            {job.attachments?.length ? (
              <ul className="list-disc pl-6 text-sm">
                {job.attachments.map((a, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => openProtectedFile(a.filename)}
                      className="text-primary-600 hover:text-primary-800 underline"
                    >
                      {a.originalName || a.filename}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-gray-500 text-sm">No attachments</div>
            )}
            </div>
          </div>
        </div>

        {/* Right: Summary & Status */}
        <div className="space-y-6">
          <div className="relative overflow-hidden rounded-2xl shadow-lg border border-green-200 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
            <div className="p-6 space-y-3">
              <h3 className="text-lg font-bold text-green-900 mb-4">Financial Summary</h3>
              <div className="flex justify-between text-sm">
                <div className="text-gray-600">Total Price</div>
                <div className="text-gray-900 font-semibold">KSh {job.totalPrice?.toFixed(2)}</div>
              </div>
              <div className="flex justify-between text-sm">
                <div className="text-gray-600">Advance</div>
                <div className="text-gray-900 font-semibold">KSh {job.advancePayment?.toFixed(2)}</div>
              </div>
            <div className="flex justify-between text-xs text-gray-500">
              <div>Advance Received</div>
              <div>{job.advanceReceivedAt ? new Date(job.advanceReceivedAt).toLocaleDateString() : '-'}</div>
            </div>
              <div className="flex justify-between text-sm">
                <div className="text-gray-600">Balance Due</div>
                <div className="text-gray-900 font-semibold">KSh {job.balanceDue?.toFixed(2)}</div>
              </div>
            <div className="flex justify-between text-sm">
              <div className="text-gray-600">Due Date</div>
              <div className="text-gray-900 font-medium">{new Date(job.expectedDueDate).toLocaleDateString()}</div>
            </div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-2xl shadow-lg border border-indigo-200 bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50">
            <div className="p-6 space-y-3">
              <h3 className="text-lg font-bold text-indigo-900">Status Management</h3>
            <select className="input-field" value={newStatus} onChange={e => setNewStatus(e.target.value)}>
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {newStatus === 'collected' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                  <select className="input-field" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                    <option value="cash">Cash</option>
                    <option value="till">Till</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount Collected</label>
                  <input className="input-field" type="number" step="0.01" value={amountCollected} onChange={e => setAmountCollected(e.target.value)} />
                </div>
              </div>
            )}
            <textarea
              className="input-field"
              rows={3}
              placeholder="Notes (optional)"
              value={statusNotes}
              onChange={e => setStatusNotes(e.target.value)}
            />
            <button className="btn-primary w-full" disabled={updating} onClick={updateStatus}>
              {updating ? 'Updating...' : 'Update Status'}
            </button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={deleteJob}
        title="Delete Job"
        message={`Are you sure you want to delete job ${job.jobId}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default JobDetail; 